import useAlert from '@/components/Alert/hooks/useAlert'
import { updateOrgUserEmail } from '../repository/updateOrgUserEmail'
import { updatePartnerGeneralInfo } from '../repository/updatePartnerGeneralInfo'
import Storage from '@/lib/Storage'
import { updateOrgUserPassword } from '../repository/updateOrgUserPassword'
import { getAccessTokenPayload } from '@/lib/jwt/decode'
import { PartnerGeneralInfo } from '../types/PartnerGeneralInfo'
import { updateVendorGeneralInfo } from '../repository/updateVendorGeneralInfo'
import { AddressData } from '../repository/getAddresses'
import { updatePartnerAddressInfo } from '../repository/updatePartnerAddressInfo'
import { requestOrgUserPasswordReset } from '../repository/requestOrgUserPasswordReset'
import { resetOrgUserPassword } from '../repository/resetOrgUserPassword'
import {
  PASSWORD_RULES,
  checkPasswordRequirements
} from '@/lib/passwordValidation'

type PasswordUpdate = {
  password: string
  confirmPassword: string
}

type PasswordResetRequest = {
  email: string
  recaptchaToken: string | null
}

type PasswordReset = {
  password: string
  confirmPassword: string
  token: string
  recaptchaToken: string | null
}

const validateInfo = (data: PartnerGeneralInfo) => {
  const errors = []

  if (!data.primaryContact) errors.push('Primary contact is required')
  if (!data.phoneNumber) errors.push('Phone number is required')

  return errors
}

const validateEmail = (email: string) => {
  const errors = []

  if (!email) errors.push('Email is required')

  return errors
}

const getPasswordRequirementErrors = (password: string) => {
  const reqs = checkPasswordRequirements(password)
  return PASSWORD_RULES.filter((rule) => !reqs[rule.key]).map(
    (rule) => rule.label
  )
}

const validatePassword = (data: PasswordUpdate) => {
  const errors = []

  if (!data.password) errors.push('Password is required')
  if (!data.confirmPassword) errors.push('Confirm password is required')
  if (data.password !== data.confirmPassword)
    errors.push('Passwords do not match')
  if (data.password) errors.push(...getPasswordRequirementErrors(data.password))

  return errors
}

const validateVendorInfo = (data: PartnerGeneralInfo) => {
  const errors = []

  if (!data.primaryContact) errors.push('Primary contact is required')
  if (!data.phoneNumber) errors.push('Phone number is required')
  if (!data.supportContactStackCommerce)
    errors.push('Support contact (StackCommerce) is required')
  if (!data.fulfillmentContact) errors.push('Fulfillment contact is required')

  return errors
}

const validatePartnerAddress = (data: AddressData) => {
  const errors = []
  if (!data.address_1) errors.push('Address is required')
  if (!data.city) errors.push('City is required')
  if (!data.state) errors.push('State is required')
  if (!data.zip) errors.push('Zip code is required')
  if (!data.country) errors.push('Country is required')

  return errors
}

const validateRequestPasswordReset = (data: PasswordResetRequest) => {
  const errors = []
  if (data.email.length === 0) errors.push('Email is required')
  return errors
}

const validateResetPassword = (data: PasswordReset) => {
  const errors = []
  if (data.password !== data.confirmPassword) {
    errors.push('Passwords do not match. Please try again.')
  }
  if (data.password) errors.push(...getPasswordRequirementErrors(data.password))
  return errors
}

const setUserEmail = (email: string) => {
  const user = Storage().getObject('user')
  Storage().setObject('user', { ...user, email })
}

const useAccount = () => {
  const { alertSuccess, alertErrors } = useAlert()

  const validate = <T>(
    data: T,
    validate: (data: T) => string[],
    onValidate: (data: T) => void,
    onReject?: (errors: string[]) => void
  ) => {
    const errors = validate(data)

    if (errors.length > 0) {
      alertErrors(errors)
      onReject?.(errors)
    } else {
      onValidate(data)
    }
  }

  const updateInfo = (info: PartnerGeneralInfo) => {
    validate(info, validateInfo, (info) => {
      updatePartnerGeneralInfo(info).then((data) => {
        if (!data.error) {
          alertSuccess('Info successfully updated!')
        } else alertErrors(['Something went wrong. Please try again.'])
      })
    })
  }

  const updateEmail = (email: string) => {
    validate(email, validateEmail, (email) => {
      updateOrgUserEmail(email).then((data) => {
        if (!data.error) {
          const email = data.data.attributes.email
          setUserEmail(email)
          alertSuccess('Email successfully updated!')
        } else alertErrors(['Something went wrong. Please try again'])
      })
    })
  }

  const updatePassword = (passwords: PasswordUpdate) => {
    validate(passwords, validatePassword, (passwords) => {
      const { password } = passwords
      const email = getAccessTokenPayload()?.data?.attributes?.email
      if (!email) {
        alertErrors(['Session expired. Please log in again.'])
        return
      }
      updateOrgUserPassword(password, email).then((data) => {
        if (data.error) {
          alertErrors(['Something went wrong. Please try again'])
        } else {
          alertSuccess('Password successfully updated!')
        }
      })
    })
  }

  const updateVendorInfo = (info: PartnerGeneralInfo) => {
    validate(info, validateVendorInfo, (info) => {
      updateVendorGeneralInfo(info).then((data) => {
        if (!data.error) {
          alertSuccess('Info successfully updated!')
        } else alertErrors(['Something went wrong. Please try again.'])
      })
    })
  }

  const updatePartnerAddress = (
    info: AddressData,
    verificationState: string
  ) => {
    validate(info, validatePartnerAddress, (info) => {
      updatePartnerAddressInfo(info, verificationState).then((data) => {
        if (!data.error) {
          alertSuccess('Info successfully updated!')
        } else alertErrors(['Something went wrong. Please try again.'])
      })
    })
  }

  const requestPasswordReset = (
    data: PasswordResetRequest,
    onSuccess?: () => void,
    onError?: () => void
  ) => {
    validate(
      data,
      validateRequestPasswordReset,
      (data) => {
        requestOrgUserPasswordReset(data).then((data) => {
          if (!data.error) {
            alertSuccess(
              'If you have an account, you will receive an email with password reset instructions.'
            )
            onSuccess?.()
          } else {
            alertErrors(['Something went wrong. Please try again.'])
            onError?.()
          }
        })
      },
      onError
    )
  }

  const resetPassword = (
    data: PasswordReset,
    onSuccess?: () => void,
    onError?: () => void
  ) => {
    validate(
      data,
      validateResetPassword,
      (data) => {
        resetOrgUserPassword(data).then((data) => {
          if (!data.error) {
            alertSuccess(
              'Password has been successfully reset. You can now log in with your new password.'
            )
            onSuccess?.()
          } else {
            alertErrors(['Something went wrong. Please try again.'])
            onError?.()
          }
        })
      },
      onError
    )
  }

  return {
    updateInfo,
    updateEmail,
    updatePassword,
    updateVendorInfo,
    updatePartnerAddress,
    requestPasswordReset,
    resetPassword
  }
}

export default useAccount
